// The detail level (DETAIL_LEVELS.md): how much of what a screen knows is on
// the face of a row or card. Pure and sport-agnostic — it names no screen and
// no stat, only how many of them ride along by default.
//
// Two rules this module exists to keep honest:
//   1. The level ONLY controls the face. Everything deeper (a popover, the
//      detail sheet, an expandable group, another screen) is reachable at
//      every level, unchanged. Nothing is unreachable, only relocated.
//   2. It never decides whether the site gives advice. See
//      docs/decisions/2026-09-12-tools-not-instructions.md.

import { z } from 'zod';

/** Ordered from least on the face to most. The order is the comparison. */
export const DETAIL_LEVELS = ['simple', 'standard', 'pro'] as const;

export type DetailLevel = (typeof DETAIL_LEVELS)[number];

export const DetailLevelSchema = z.enum(DETAIL_LEVELS);

/**
 * Simple, decided 2026-09-21 under the taste-call delegation, reversing the
 * provisional Standard of 2026-09-12.
 *
 * THE ARGUMENT IS AN ASYMMETRY, NOT A PREFERENCE. A manager who wants more
 * data and lands on Simple notices in seconds and fixes it in one click — the
 * screen visibly has room and the control is in the account menu. A casual
 * manager who lands on Standard and finds it dense does not know a control
 * exists; they conclude the site is not for them, and that conclusion is
 * silent. Choose the recoverable failure.
 *
 * The first-run picker (SCENARIOS L4) retires this constant for anybody who
 * answers it. Until then it is the floor. DETAIL_LEVELS.md lists what would
 * move it.
 */
export const DEFAULT_DETAIL: DetailLevel = 'simple';

/**
 * What a person sees this level called. THE ONLY COPY — a second one drifts.
 *
 * "Everything" rather than "Pro" (2026-09-21): on a site with no paid tier
 * "Pro" names a status the reader either has or lacks, and the one who lacks
 * it reads Simple as the lesser seat, which is the exact feeling Ken asked to
 * avoid. "Everything" names what you get, and it is accurate.
 *
 * THE STORED VALUE STAYS `'pro'`. Same split CLAUDE.md draws between the
 * product name and the frozen slug: the label is the product, the key is
 * plumbing. Renaming the key would rewrite `fsp.detail.v1` in every reader's
 * browser for a cosmetic gain.
 */
export const DETAIL_LABEL: Record<DetailLevel, string> = {
  simple: 'Simple',
  standard: 'Standard',
  pro: 'Everything',
};

const RANK: Record<DetailLevel, number> = { simple: 0, standard: 1, pro: 2 };

/** True when `level` is at or past `min`. The test behind `<Detail min="pro">`. */
export function atLeast(level: DetailLevel, min: DetailLevel): boolean {
  return RANK[level] >= RANK[min];
}

/**
 * Which pole a panel opens at. Pro opens on the spreadsheet; the other two
 * open zen. A manager can always flip one panel either way — that is the
 * panel's own switch, not this.
 */
export function opensAsSheet(level: DetailLevel): boolean {
  return level === 'pro';
}

export interface DetailMetrics {
  /** Row height in px (VISUAL_IDENTITY.md §3). Fewer things, more room each. */
  rowHeight: number;
  /** Panel body padding in px. */
  panelPad: number;
}

/**
 * The survivors get the space the removed ones used to take, which is what
 * makes Simple calm rather than sparse.
 */
export const DETAIL_METRICS: Record<DetailLevel, DetailMetrics> = {
  simple: { rowHeight: 60, panelPad: 16 },
  standard: { rowHeight: 52, panelPad: 12 },
  pro: { rowHeight: 40, panelPad: 8 },
};

/**
 * Parse a stored value, falling back to the default. A corrupt display
 * preference is not worth failing a page load over; provider data still
 * fails loudly, which is what the "fail loudly" rule is about.
 */
export function parseDetailLevel(value: unknown): DetailLevel {
  const parsed = DetailLevelSchema.safeParse(value);
  return parsed.success ? parsed.data : DEFAULT_DETAIL;
}
